import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Brain,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Activity,
  Copy,
  Zap,
  Target,
  RefreshCw,
  Loader2,
  Sparkles,
  ArrowRight,
  Search,
  Edit3,
  Shield,
  Flame,
  Gauge,
  Calendar,
  Lightbulb,
  Crown,
  Users,
  Clock,
  Wand2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import ViralScoreDialog from "../components/algo/ViralScoreDialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Bar,
  BarChart,
  Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  "alert-triangle": AlertTriangle,
  search: Search,
  "edit-3": Edit3,
  shield: Shield,
};

// Momentum gauge widget
const MomentumGauge = ({ score, direction, darkMode }) => {
  const normalized = (score + 100) / 2; // 0 to 100
  const angle = -90 + (normalized * 1.8); // -90 to 90 degrees
  const color =
    score >= 50 ? "#10b981" :
    score >= 20 ? "#84cc16" :
    score >= -20 ? "#94a3b8" :
    score >= -50 ? "#f59e0b" : "#ef4444";

  return (
    <div className="flex flex-col items-center" data-testid="momentum-gauge">
      <svg width="200" height="120" viewBox="0 0 200 120">
        {/* Background arc */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke={darkMode ? "#21262d" : "#e5e7eb"}
          strokeWidth="14"
          strokeLinecap="round"
        />
        {/* Score arc */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke={color}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray="251.2"
          strokeDashoffset={251.2 - (normalized / 100) * 251.2}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
        {/* Needle */}
        <line
          x1="100"
          y1="100"
          x2={100 + 60 * Math.cos((angle * Math.PI) / 180)}
          y2={100 + 60 * Math.sin((angle * Math.PI) / 180)}
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          style={{ transition: "all 0.6s ease" }}
        />
        <circle cx="100" cy="100" r="6" fill={color} />
        {/* Score text */}
        <text x="100" y="80" textAnchor="middle" fill={darkMode ? "#fff" : "#111"} fontSize="32" fontWeight="bold">
          {score > 0 ? `+${Math.round(score)}` : Math.round(score)}
        </text>
      </svg>
      <div className="text-center mt-2">
        <p className={`text-lg font-bold`} style={{ color }}>{direction}</p>
        <p className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Market Momentum Index</p>
      </div>
    </div>
  );
};

// Threat score card
const ThreatBadge = ({ level }) => {
  const colors = {
    critical: "bg-red-500/20 text-red-400 border-red-500/40",
    high: "bg-orange-500/20 text-orange-400 border-orange-500/40",
    medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
    low: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  };
  return (
    <Badge variant="outline" className={`${colors[level] || colors.low} border`} data-testid={`threat-badge-${level}`}>
      {level?.toUpperCase()}
    </Badge>
  );
};

export default function AlgorithmicIntelligence() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: "UAE", brand: "blackswan" };

  const [actionQueue, setActionQueue] = useState([]);
  const [threatScores, setThreatScores] = useState({ competitors: [], summary: {} });
  const [anomalies, setAnomalies] = useState({ anomalies: [], summary: {} });
  const [momentum, setMomentum] = useState(null);
  const [copycats, setCopycats] = useState({ patterns: [], top_originators: [], top_copycats: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("queue");

  // Viral score state
  const [viralOpen, setViralOpen] = useState(false);
  const [viralInput, setViralInput] = useState("");
  const [viralLoading, setViralLoading] = useState(false);
  const [viralResult, setViralResult] = useState(null);

  const fetchAll = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      const [aq, ts, an, mo, cc] = await Promise.all([
        axios.get(`${API}/algo/action-queue/${brand}/${region}`),
        axios.get(`${API}/algo/competitor-scores/${brand}/${region}`),
        axios.get(`${API}/algo/anomaly-detection/${brand}/${region}`),
        axios.get(`${API}/algo/momentum-index/${brand}/${region}`),
        axios.get(`${API}/algo/copycat-detection/${brand}/${region}`),
      ]);
      setActionQueue(aq.data.queue || []);
      setThreatScores({ competitors: ts.data.competitors || [], summary: ts.data.summary || {} });
      setAnomalies({ anomalies: an.data.anomalies || [], summary: an.data.summary || {} });
      setMomentum(mo.data);
      setCopycats(cc.data);
      if (showToast) toast.success("Algo intelligence refreshed");
    } catch (e) {
      console.error("Algo fetch error:", e);
      if (showToast) toast.error("Failed to refresh");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [brand, region]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const calcViralScore = async () => {
    if (!viralInput.trim()) {
      toast.error("Enter content to score");
      return;
    }
    setViralLoading(true);
    try {
      const res = await axios.post(`${API}/algo/viral-score/${brand}/${region}`, {
        primary_post: viralInput,
        topic: viralInput.slice(0, 80),
      });
      setViralResult(res.data);
    } catch (e) {
      toast.error("Failed to calculate viral score");
    } finally {
      setViralLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC143C]" />
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 ${darkMode ? "text-white" : "text-slate-900"}`} data-testid="algorithmic-intelligence-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Brain className="w-8 h-8 text-[#DC143C]" />
            Algorithmic Intelligence
          </h1>
          <p className={`mt-1 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
            Real-time anomaly detection, predictive scoring, and pattern recognition across {region}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ViralScoreDialog
            open={viralOpen}
            onOpenChange={setViralOpen}
            darkMode={darkMode}
            viralInput={viralInput}
            setViralInput={setViralInput}
            viralResult={viralResult}
            viralLoading={viralLoading}
            calcViralScore={calcViralScore}
          />
          <Button onClick={() => fetchAll(true)} disabled={refreshing} variant="outline" className="gap-2" data-testid="refresh-algo-btn">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Top metrics row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`${darkMode ? "bg-[#161b22] border-[#30363d]" : ""}`} data-testid="metric-momentum">
          <CardContent className="p-4">
            <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-1 font-semibold ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
              <Gauge className="w-3 h-3" /> Momentum
            </div>
            <div className="flex items-end gap-2">
              <p className="text-2xl font-bold" style={{ color: momentum?.score >= 0 ? "#10b981" : "#ef4444" }}>
                {momentum?.score > 0 ? "+" : ""}{momentum?.score ?? 0}
              </p>
              <p className={`text-xs mb-1 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{momentum?.direction || "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${darkMode ? "bg-[#161b22] border-[#30363d]" : ""}`} data-testid="metric-threats">
          <CardContent className="p-4">
            <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-1 font-semibold ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
              <Shield className="w-3 h-3" /> Critical Threats
            </div>
            <p className="text-2xl font-bold text-red-400">
              {threatScores.summary?.critical_threats || 0}
              <span className={`text-sm font-normal ml-2 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                / {threatScores.summary?.total || 0}
              </span>
            </p>
          </CardContent>
        </Card>
        <Card className={`${darkMode ? "bg-[#161b22] border-[#30363d]" : ""}`} data-testid="metric-anomalies">
          <CardContent className="p-4">
            <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-1 font-semibold ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
              <Activity className="w-3 h-3" /> Anomalies
            </div>
            <p className="text-2xl font-bold text-orange-400">{anomalies.summary?.total_anomalies || 0}</p>
          </CardContent>
        </Card>
        <Card className={`${darkMode ? "bg-[#161b22] border-[#30363d]" : ""}`} data-testid="metric-actions">
          <CardContent className="p-4">
            <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-1 font-semibold ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
              <Zap className="w-3 h-3" /> Action Queue
            </div>
            <p className="text-2xl font-bold text-amber-400">{actionQueue.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid grid-cols-5 ${darkMode ? "bg-[#161b22]" : ""}`}>
          <TabsTrigger value="queue" data-testid="tab-queue">Action Queue</TabsTrigger>
          <TabsTrigger value="momentum" data-testid="tab-momentum">Momentum</TabsTrigger>
          <TabsTrigger value="threats" data-testid="tab-threats">Threat Scores</TabsTrigger>
          <TabsTrigger value="anomalies" data-testid="tab-anomalies">Anomalies</TabsTrigger>
          <TabsTrigger value="copycats" data-testid="tab-copycats">Copycat Patterns</TabsTrigger>
        </TabsList>

        {/* ACTION QUEUE */}
        <TabsContent value="queue" className="mt-6">
          <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Top Priority Actions
              </CardTitle>
              <CardDescription className={darkMode ? "text-slate-400" : ""}>
                Auto-prioritized by urgency × impact / effort across all signals
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {actionQueue.length === 0 ? (
                <p className={`text-sm ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                  No urgent actions right now. You're all caught up.
                </p>
              ) : (
                actionQueue.map((a, i) => {
                  const Icon = ICON_MAP[a.icon] || Target;
                  return (
                    <div
                      key={`${a.competitor_id || a.title || "action"}-${i}`}
                      className={`flex items-start gap-3 p-4 rounded-lg border ${darkMode ? "bg-[#0d1117] border-[#30363d] hover:border-[#DC143C]/40" : "bg-white hover:border-slate-300"} transition-colors`}
                      data-testid={`action-item-${i}`}
                    >
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        a.urgency >= 90 ? "bg-red-500/20 text-red-400" :
                        a.urgency >= 70 ? "bg-orange-500/20 text-orange-400" :
                        "bg-amber-500/20 text-amber-400"
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <p className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>{a.action}</p>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge variant="outline" className={`text-xs ${darkMode ? "border-[#30363d] text-slate-300" : ""}`}>{a.category}</Badge>
                            <Badge variant="outline" className={`text-xs gap-1 ${darkMode ? "border-[#30363d] text-slate-300" : ""}`}>
                              <Clock className="w-3 h-3" /> {a.deadline}
                            </Badge>
                          </div>
                        </div>
                        <p className={`text-xs mt-1 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{a.context}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs">
                          <span className={darkMode ? "text-slate-400" : "text-slate-600"}>
                            Urgency: <span className="font-bold text-red-400">{a.urgency}</span>
                          </span>
                          <span className={darkMode ? "text-slate-400" : "text-slate-600"}>
                            Impact: <span className="font-bold text-emerald-400">{a.impact}</span>
                          </span>
                          <span className={darkMode ? "text-slate-400" : "text-slate-600"}>
                            Effort: <span className="font-bold text-blue-400">{a.effort}</span>
                          </span>
                          <span className="ml-auto font-bold text-[#DC143C]">Score: {a.priority_score}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MOMENTUM */}
        <TabsContent value="momentum" className="mt-6 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className={`lg:col-span-1 ${darkMode ? "bg-[#161b22] border-[#30363d]" : ""}`}>
              <CardHeader>
                <CardTitle>Current Index</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                {momentum && (
                  <MomentumGauge score={momentum.score} direction={momentum.direction} darkMode={darkMode} />
                )}
              </CardContent>
            </Card>
            <Card className={`lg:col-span-2 ${darkMode ? "bg-[#161b22] border-[#30363d]" : ""}`}>
              <CardHeader>
                <CardTitle>Component Breakdown</CardTitle>
                <CardDescription className={darkMode ? "text-slate-400" : ""}>
                  Decomposition of the momentum score
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={momentum ? Object.entries(momentum.components).map(([k, v]) => ({
                    name: k.replace(/_/g, " "),
                    value: v,
                  })) : []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#30363d" : "#e5e7eb"} />
                    <XAxis type="number" stroke={darkMode ? "#8b949e" : "#6b7280"} />
                    <YAxis dataKey="name" type="category" stroke={darkMode ? "#8b949e" : "#6b7280"} width={120} fontSize={11} />
                    <Tooltip contentStyle={{ background: darkMode ? "#0d1117" : "#fff", border: "1px solid #30363d" }} />
                    <Bar dataKey="value" fill="#DC143C">
                      {momentum && Object.entries(momentum.components).map(([k, v]) => (
                        <Cell key={k} fill={v >= 0 ? "#10b981" : "#ef4444"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {momentum?.interpretation && (
                  <div className="mt-4 space-y-1 text-xs">
                    {Object.entries(momentum.interpretation).map(([k, v]) => (
                      <p key={k} className={darkMode ? "text-slate-400" : "text-slate-600"}>
                        <span className="font-semibold">{k.replace(/_/g, " ")}:</span> {v}
                      </p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {momentum?.history?.length > 1 && (
            <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
              <CardHeader>
                <CardTitle>Momentum History</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={momentum.history}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#30363d" : "#e5e7eb"} />
                    <XAxis dataKey="date" stroke={darkMode ? "#8b949e" : "#6b7280"} fontSize={11} />
                    <YAxis stroke={darkMode ? "#8b949e" : "#6b7280"} domain={[-100, 100]} />
                    <Tooltip contentStyle={{ background: darkMode ? "#0d1117" : "#fff", border: "1px solid #30363d" }} />
                    <Line type="monotone" dataKey="score" stroke="#DC143C" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* THREAT SCORES */}
        <TabsContent value="threats" className="mt-6">
          <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                Competitor Threat Scores
              </CardTitle>
              <CardDescription className={darkMode ? "text-slate-400" : ""}>
                Algorithmic ranking based on social activity, engagement, freshness, and market position
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {threatScores.competitors.slice(0, 25).map((c, i) => (
                  <div
                    key={c.id || i}
                    className={`flex items-center gap-3 p-3 rounded-lg ${darkMode ? "bg-[#0d1117] hover:bg-[#1a1f2a]" : "bg-slate-50 hover:bg-slate-100"} transition-colors`}
                    data-testid={`threat-row-${i}`}
                  >
                    <div className={`w-8 text-center font-bold ${darkMode ? "text-slate-500" : "text-slate-400"}`}>#{i + 1}</div>
                    <div className="flex-1">
                      <p className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>{c.name}</p>
                      {c.website && <p className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{c.website}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      <ThreatBadge level={c.threat_level} />
                      <div className="text-right w-16">
                        <p className="font-bold text-lg" style={{
                          color: c.threat_score >= 80 ? "#ef4444" : c.threat_score >= 60 ? "#f97316" : c.threat_score >= 40 ? "#f59e0b" : "#10b981"
                        }}>
                          {c.threat_score}
                        </p>
                        <p className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-400"}`}>/ 100</p>
                      </div>
                    </div>
                  </div>
                ))}
                {threatScores.competitors.length === 0 && (
                  <p className={`text-sm text-center py-8 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                    No competitor data available yet
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ANOMALIES */}
        <TabsContent value="anomalies" className="mt-6">
          <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-orange-500" />
                Statistical Anomalies
              </CardTitle>
              <CardDescription className={darkMode ? "text-slate-400" : ""}>
                Z-score based detection of competitors with unusual posting frequency or engagement spikes (last {anomalies.summary?.lookback_days || 30} days)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {anomalies.anomalies.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className={`w-10 h-10 mx-auto mb-2 ${darkMode ? "text-slate-600" : "text-slate-300"}`} />
                  <p className={`text-sm ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                    No anomalies detected — all competitors are within their normal patterns.
                  </p>
                </div>
              ) : (
                anomalies.anomalies.map((a, i) => (
                  <div
                    key={a.competitor_id || `${a.competitor_name}-${i}`}
                    className={`p-4 rounded-lg border ${darkMode ? "bg-[#0d1117] border-[#30363d]" : "bg-white border-slate-200"} ${a.severity === "critical" ? "border-l-4 border-l-red-500" : "border-l-4 border-l-orange-500"}`}
                    data-testid={`anomaly-${i}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>{a.competitor_name}</p>
                          <ThreatBadge level={a.severity} />
                        </div>
                        <ul className="mt-2 space-y-1 text-sm">
                          {a.anomaly_types.map((t) => (
                            <li key={t} className={darkMode ? "text-slate-300" : "text-slate-700"}>• {t}</li>
                          ))}
                        </ul>
                        <p className={`text-xs mt-2 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                          {a.recommendation}
                        </p>
                      </div>
                      <div className="text-right text-xs space-y-1 flex-shrink-0">
                        <div>
                          <span className={darkMode ? "text-slate-400" : "text-slate-500"}>Posts z-score:</span>
                          <span className="ml-1 font-bold">{a.posting_z_score}</span>
                        </div>
                        <div>
                          <span className={darkMode ? "text-slate-400" : "text-slate-500"}>Eng z-score:</span>
                          <span className="ml-1 font-bold">{a.engagement_z_score}</span>
                        </div>
                        <div>
                          <span className={darkMode ? "text-slate-400" : "text-slate-500"}>Latest:</span>
                          <span className="ml-1 font-bold">{a.latest_post_count}</span>
                          <span className="opacity-60"> vs {a.baseline_avg} avg</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COPYCATS */}
        <TabsContent value="copycats" className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-500" />
                  Top Originators
                </CardTitle>
                <CardDescription className={darkMode ? "text-slate-400" : ""}>
                  Competitors who pioneer topics that others follow
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {copycats.top_originators?.length === 0 ? (
                    <p className={`text-sm ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                      No clear originators detected yet
                    </p>
                  ) : copycats.top_originators?.map((o, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded ${darkMode ? "bg-[#0d1117]" : "bg-slate-50"}`}>
                      <span className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{o.name}</span>
                      <Badge variant="outline" className="border-yellow-500/40 text-yellow-400">
                        {o.originated_topics} topics
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Copy className="w-5 h-5 text-purple-500" />
                  Top Copycats
                </CardTitle>
                <CardDescription className={darkMode ? "text-slate-400" : ""}>
                  Competitors who replicate others' content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {copycats.top_copycats?.length === 0 ? (
                    <p className={`text-sm ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                      No copycat behaviour detected
                    </p>
                  ) : copycats.top_copycats?.map((c, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded ${darkMode ? "bg-[#0d1117]" : "bg-slate-50"}`}>
                      <span className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{c.name}</span>
                      <Badge variant="outline" className="border-purple-500/40 text-purple-400">
                        {c.copied_topics} copies
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
            <CardHeader>
              <CardTitle>Copycat Patterns Detected</CardTitle>
              <CardDescription className={darkMode ? "text-slate-400" : ""}>
                {copycats.summary?.posts_analyzed || 0} posts analyzed · {copycats.summary?.patterns_found || 0} patterns found
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {copycats.patterns?.length === 0 ? (
                <p className={`text-sm ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                  No copycat patterns detected yet
                </p>
              ) : copycats.patterns.map((p, i) => (
                <div
                  key={p.originator?.competitor_id || p.originator?.competitor_name || `pattern-${i}`}
                  className={`p-3 rounded-lg ${darkMode ? "bg-[#0d1117]" : "bg-slate-50"}`}
                  data-testid={`pattern-${i}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-4 h-4 text-yellow-500" />
                    <span className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>{p.originator?.competitor_name}</span>
                    <ArrowRight className="w-4 h-4 opacity-50" />
                    <Users className="w-4 h-4 text-purple-500" />
                    <span className={darkMode ? "text-slate-400" : "text-slate-500"}>
                      {p.copycats?.length} copycat{p.copycats?.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <p className={`text-xs mb-2 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                    {p.originator?.post_summary}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {p.topic_keywords?.slice(0, 6).map((k, j) => (
                      <Badge key={j} variant="outline" className="text-xs">{k}</Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {p.copycats?.map((c, j) => (
                      <span key={j} className={`px-2 py-1 rounded ${darkMode ? "bg-purple-500/10 text-purple-400" : "bg-purple-100 text-purple-700"}`}>
                        {c.competitor_name} ({c.lag_days}d lag, {(c.similarity * 100).toFixed(0)}% match)
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
